// Renders one PAST exchange from `useAgenticRunController`'s `history` array (see
// `../useAgenticRunController.js`'s header comment for the scrollback design) — the user's
// goal, the decoded final answer, and any frozen Pure LLM chart-mutation preview. Live
// reasoning/steps are only useful to watch a run unfold in real time, so only the current/live
// turn (rendered separately) shows them; past turns already ran to completion.
import React from 'react';
import {Alert, Box, Paper, Stack} from '@mui/material';
import AgenticFinalAnswer from './AgenticFinalAnswer';
import PureLlmMutationPreview, {isMovesOnlyMarkup} from './PureLlmMutationPreview';
import {AGENTIC_RUN_STATUS, normalizeChartMutationMode} from '../useAgenticRunController';

/**
 * @param {{
 *   exchanges: Array<object>,
 *   chartId?: string,
 *   onSelectComponent?: Function,
 *   apiBaseUrl?: string,
 * }} props
 */
const AgenticExchangeHistory = ({
    exchanges = [],
    chartId,
    onSelectComponent,
    apiBaseUrl,
}) => {
    if (!exchanges.length) {
        return null;
    }

    return (
        <>
            {exchanges.map((exchange) => {
                const chartMarkup = typeof exchange.chartMarkup === 'string'
                    ? exchange.chartMarkup
                    : null;
                const mutationMode = chartMarkup
                    ? normalizeChartMutationMode(exchange.mutationMode)
                    : null;
                const showFrozenPreview = Boolean(
                    chartMarkup
                    && mutationMode === 'confirm'
                    && !isMovesOnlyMarkup(chartMarkup)
                );

                return (
                    <Stack key={exchange.id} spacing={1} data-testid="agentic-past-exchange">
                        <Paper elevation={0} sx={{py: 1}}>
                            <Box sx={{fontWeight: 600, fontSize: 14 * 1.25}}>{exchange.goal}</Box>
                        </Paper>

                        {exchange.status === AGENTIC_RUN_STATUS.FAILED && exchange.error ? (
                            <Alert severity="error">{exchange.error}</Alert>
                        ) : (
                            <>
                                <AgenticFinalAnswer
                                    text={exchange.finalAnswerText}
                                    citations={exchange.citations}
                                    onSelectComponent={onSelectComponent}
                                    runId={exchange.runId || null}
                                    initialRating={exchange.userReaction || null}
                                    apiBaseUrl={apiBaseUrl}
                                />
                                {showFrozenPreview ? (
                                    <PureLlmMutationPreview
                                        markup={chartMarkup}
                                        chartId={chartId}
                                        frozen
                                        initialOpStatuses={exchange.mutationPreviewOpStatuses || undefined}
                                        // This is a PAST, already-applied turn re-rendered on every
                                        // chat-history load (e.g. page refresh). The op's x/y/near
                                        // placement decision already happened; re-parsing its raw
                                        // markup here is only to redraw the frozen summary, so
                                        // "will use viewport fallback" parse warnings are noise —
                                        // they were only ever useful while the turn was live.
                                        suppressParseWarnings
                                        onReject={() => {}}
                                    />
                                ) : null}
                            </>
                        )}
                    </Stack>
                );
            })}
        </>
    );
};

export default AgenticExchangeHistory;
